/**
 * Pokémon GO's weather boost table: which weather boosts which types.
 *
 * A raid boss caught under boosting weather is level 25 instead of 20, and
 * that is the whole reason a 100% catch CP has two figures. The table is
 * fixed in the game (seven weathers, eighteen types, each type boosted by
 * exactly one weather), so it lives here as data rather than a feed.
 */
import type { PokeType } from "./types";

export type WeatherKind =
  | "sunny"
  | "rainy"
  | "partly_cloudy"
  | "cloudy"
  | "windy"
  | "snow"
  | "fog";

export const WEATHER_LABELS: Readonly<Record<WeatherKind, string>> = {
  sunny: "Sunny / Clear",
  rainy: "Rain",
  partly_cloudy: "Partly cloudy",
  cloudy: "Cloudy",
  windy: "Windy",
  snow: "Snow",
  fog: "Fog",
};

export const WEATHER_BOOSTED_TYPES: Readonly<Record<WeatherKind, readonly PokeType[]>> = {
  sunny: ["grass", "ground", "fire"],
  rainy: ["water", "electric", "bug"],
  partly_cloudy: ["normal", "rock"],
  cloudy: ["fairy", "fighting", "poison"],
  windy: ["dragon", "flying", "psychic"],
  snow: ["ice", "steel"],
  fog: ["dark", "ghost"],
};

/** The weather that boosts a type. */
export function weatherForType(type: PokeType): WeatherKind {
  for (const [weather, types] of Object.entries(WEATHER_BOOSTED_TYPES) as Array<[WeatherKind, readonly PokeType[]]>) {
    if (types.includes(type)) return weather;
  }
  return "sunny";
}

/**
 * Every weather that boosts a Pokémon of these types, with the types each
 * one boosts, in table order. A dual type can have two.
 */
export function boostingWeathers(types: readonly string[]): Array<{ weather: WeatherKind; types: PokeType[] }> {
  const out: Array<{ weather: WeatherKind; types: PokeType[] }> = [];
  for (const [weather, boosted] of Object.entries(WEATHER_BOOSTED_TYPES) as Array<[WeatherKind, readonly PokeType[]]>) {
    const hit = boosted.filter((t) => types.includes(t));
    if (hit.length > 0) out.push({ weather, types: hit });
  }
  return out;
}
